import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../Api/apiAxios";
import Loading from "../../Components/Loading";
import Mensagem from "../../Components/Mensagem";
import { MyContext } from "../../Context/MyContext";
import { PedidosContext } from "../../Context/PedidosContext";

import {
  Box,
  Button,
  Chip,
  Collapse,
  Container,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import HistoryIcon from "@mui/icons-material/History";

import { format } from "date-fns";

import { FormSectionBox, FormSectionTitle } from "../Pedidos/styles";
import { StyledTableCell } from "../Pedidos/components/acesso/acessoIndex";
import { TextFieldBase, InputLabelSelect, SelectBase } from "../Pedidos/styles";
import { ensureContratoDistribuidorId } from "./shared/ensureContratoDistribuidorId";

const toDateBR = (value) => {
  if (!value) return "";
  try {
    return format(new Date(value), "dd/MM/yyyy");
  } catch {
    return "";
  }
};

const statusLabel = (statusAtual) => {
  if (statusAtual === 1) return { label: "Ativo", color: "success" };
  if (statusAtual === 0) return { label: "Cancelado", color: "error" };
  return { label: "Baixado", color: "default" };
};

const tipoContratoLabel = (tipoContrato) => {
  if (tipoContrato === 0) return "Manutenção";
  if (tipoContrato === 1) return "Locação";
  if (tipoContrato === 2) return "Saas";
  return "";
};

const sistemaLabel = (sistema) => {
  if (sistema === "PONTO") return "BmaPonto";
  if (sistema === "ACESSO") return "BmaAcesso";
  if (sistema === "FOLHA") return "BmaFolha";
  return sistema || "";
};

const ViewContrato = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { mensagemGlobal, setMensagemGlobal, tipoMensagemGlobal } = useContext(MyContext);
  const { motivosCancelamento } = useContext(PedidosContext);

  const [loading, setLoading] = useState(false);
  const [contrato, setContrato] = useState(null);

  const [cliente, setCliente] = useState(null);
  const [distribuidor, setDistribuidor] = useState(null);

  const [pedidoInfo, setPedidoInfo] = useState({
    dataEntradaPedido: "",
    dataLiberacaoPedido: "",
    faixaFuncionarios: "",
    tipoVertical: "",
    tipoInstalacao: "",
    valorPedido: "",
  });

  const [negociacoes, setNegociacoes] = useState([]);

  const [historicoOpen, setHistoricoOpen] = useState(true);
  const [historicoLoading, setHistoricoLoading] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [histPagina, setHistPagina] = useState(1);
  const [histTotal, setHistTotal] = useState(0);

  const HIST_ITENS_POR_BLOCO = 3;
  const [histVisibleCount, setHistVisibleCount] = useState(HIST_ITENS_POR_BLOCO);

  const histItensPorPagina = 20;

  const contratoStatus = useMemo(() => {
    if (!contrato) return { label: "", color: "default" };
    return statusLabel(Number(contrato.statusAtual ?? 0));
  }, [contrato]);

  const getMotivoCancelamentoDescricao = React.useMemo(() => {
    const map = new Map(
      (motivosCancelamento ?? []).map((m) => [String(m.id), m.descricaoMotivo])
    );

    return (motivoCancelamentoOpcao) => {
      if (!motivoCancelamentoOpcao) return "";
      const key = String(motivoCancelamentoOpcao);
      return map.get(key) || key;
    };
  }, [motivosCancelamento]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const resp = await api.get(`/Contrato/${id}`);
        const c = resp.data;

        const usuarioId = Number(window.localStorage.getItem("usuarioId") ?? 0);
        const { contrato: contratoFix, cliente: cli, distribuidor: dist } = await ensureContratoDistribuidorId({
          api,
          contrato: c,
          usuarioId,
          cacheClienteById: new Map(),
          cacheDistribuidorById: new Map(),
        });

        setContrato(contratoFix);
        setCliente(cli);
        setDistribuidor(dist);

        if (contratoFix?.pedidoId > 0) {
          const idPedido = Number(contratoFix.pedidoId);

          if (contratoFix.sistema === "PONTO") {
            const { data } = await api.get(`/BmaPonto/${idPedido}`);
            setPedidoInfo({
              dataEntradaPedido: toDateBR(data.dataPedido),
              dataLiberacaoPedido: toDateBR(data.dataLiberacao),
              faixaFuncionarios: data.faixaFuncionarios ?? "",
              tipoVertical: "",
              tipoInstalacao: data.tipoInstalacao ?? "",
              valorPedido: data.valorMensal ?? "",
            });
          } else if (contratoFix.sistema === "ACESSO") {
            const { data } = await api.get(`/BmaAcesso/${idPedido}`);
            setPedidoInfo({
              dataEntradaPedido: toDateBR(data.dataPedido),
              dataLiberacaoPedido: toDateBR(data.dataLiberacao),
              faixaFuncionarios: "",
              tipoVertical: data.tipoVertical ?? "",
              tipoInstalacao: "",
              valorPedido: data.valorMensal ?? "",
            });
          } else if (contratoFix.sistema === "FOLHA") {
            const { data } = await api.get(`/BmaFolha/${idPedido}`);
            setPedidoInfo({
              dataEntradaPedido: toDateBR(data.dataPedido),
              dataLiberacaoPedido: toDateBR(data.dataLiberacao),
              faixaFuncionarios: data.faixaFuncionarios ?? "",
              tipoVertical: "",
              tipoInstalacao: "",
              valorPedido: data.valorMensal ?? "",
            });
          }
        }

        try {
          if (c?.id) {
            const tipoSistema =
              c.sistema === "ACESSO" ? "Acesso" : c.sistema === "PONTO" ? "Ponto" : "Folha";

            const { data } = await api.get(
              `/NegociacaoContrato/contrato-principal?idContrato=${c.id}&tipoSistema=${tipoSistema}`
            );

            const ordered = Array.isArray(data)
              ? [...data].sort((a, b) => new Date(b.periodoInicial) - new Date(a.periodoInicial))
              : [];

            setNegociacoes(ordered);
          } else {
            setNegociacoes([]);
          }
        } catch (e) {
          console.error("Erro ao buscar negociações:", e);
          setNegociacoes([]);
        }
      } catch (e) {
        console.error(e);
        alert(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const loadHistorico = async (pagina, append) => {
    if (!contrato?.id) return;

    setHistoricoLoading(true);
    try {
      const { data } = await api.get(
        `/ContratoHistorico/consulta-paginada?contratoId=${contrato.id}&pagina=${pagina}&itensPorPagina=${histItensPorPagina}`
      );

      const itens = data?.itens ?? [];
      const total = data?.total ?? 0;

      setHistTotal(total);
      setHistPagina(pagina);

      setHistorico((prev) => (append ? [...prev, ...itens] : itens));
    } catch (e) {
      console.error("Erro ao carregar histórico:", e);
    } finally {
      setHistoricoLoading(false);
    }
  };

  useEffect(() => {
    if (!contrato?.id) return;
    setHistVisibleCount(HIST_ITENS_POR_BLOCO);
    loadHistorico(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contrato?.id]);

  const canLoadMoreHistorico = historico.length < histTotal;

  const totalItens = historico.length;
  const visibleItens = historico.slice(0, histVisibleCount);
  const hasMoreVisible = histVisibleCount < totalItens;

  const handleVerMais = () => {
    setHistVisibleCount((v) => Math.min(v + HIST_ITENS_POR_BLOCO, totalItens));
  };

  const handleCarregarMaisServidor = async () => {
    await loadHistorico(histPagina + 1, true);
    setHistVisibleCount((v) => v + HIST_ITENS_POR_BLOCO);
  };

  if (loading) return <Loading />;

  return (
    <Box>
      {mensagemGlobal && (
        <Mensagem mensagem={mensagemGlobal} setMensagem={setMensagemGlobal} variant={tipoMensagemGlobal} />
      )}

      <Container sx={{ py: 2 }}>
        <Stack direction="row" justifyContent="left" alignItems="center" sx={{ mt: 1, gap: 2 }}>
          <Box
            sx={{
              backgroundColor: "#F7F8F8",
              padding: "8px 16px",
              borderStartEndRadius: "8px",
              borderStartStartRadius: "8px",
              borderBottom: "2px solid rgb(19, 35, 80)",
            }}
          >
            <Typography component="h1" variant="h6" sx={{ fontWeight: 400, color: "#a3a3a3" }}>
              Contrato
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            {contrato && (
              <Chip label={contratoStatus.label} color={contratoStatus.color} variant="filled" size="small" />
            )}
          </Stack>
        </Stack>

        <Paper sx={{ backgroundColor: "#F7F8F8" }} elevation={2}>
          <Box sx={{ p: 2 }}>
            <Divider sx={{ my: 1.5 }} />

            <FormSectionBox>
              <FormSectionTitle label="Dados do Contrato" />

              <Container sx={{ my: 3 }}>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    width: "100%",
                    maxWidth: "100%",
                    overflow: "hidden",
                    paddingTop: 1,
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(4, minmax(0, 1fr))",
                    },
                  }}
                >
                  <Box sx={{ gridColumn: { xs: "1 / -1", lg: "span 1" } }}>
                    <TextFieldBase label="Tipo de Sistema" value={sistemaLabel(contrato?.sistema)} width="100%" disabled />
                  </Box>

                  <Box sx={{ gridColumn: { xs: "1 / -1", lg: "span 3" } }}>
                    <TextFieldBase
                      label="Revenda"
                      value={distribuidor?.nomeFantasia ?? distribuidor?.razaoSocial ?? ""}
                      width="100%"
                      disabled
                    />
                  </Box>

                  <Box sx={{ gridColumn: { xs: "1 / -1", lg: "span 3" } }}>
                    <TextFieldBase
                      label="Cliente"
                      value={cliente?.nomeFantasia ?? cliente?.razaoSocial ?? ""}
                      width="100%"
                      disabled
                    />
                  </Box>

                  <Box sx={{ gridColumn: { xs: "1 / -1", lg: "span 1" } }}>
                    <TextFieldBase label="CNPJ / CPF" value={cliente?.numeroInscricao ?? ""} width="100%" disabled />
                  </Box>

                  <TextFieldBase
                    label="Número de Série"
                    value={contrato?.numeroSerie ?? ""}
                    width="100%"
                    textAlign="center"
                    disabled
                  />

                  <TextFieldBase
                    label="Data de Início"
                    value={toDateBR(contrato?.dataInicio)}
                    width="100%"
                    textAlign="center"
                    disabled
                  />

                  <TextFieldBase
                    label="Data da Última Atualização"
                    value={toDateBR(contrato?.dataUltimaAtualizacao)}
                    width="100%"
                    textAlign="center"
                    disabled
                  />

                  <TextFieldBase
                    label="Data de Cancelamento"
                    value={contrato?.dataCancelamento ? toDateBR(contrato.dataCancelamento) : "* * * * * *"}
                    width="100%"
                    textAlign="center"
                    disabled
                  />

                  <TextFieldBase
                    label="Tipo de Contrato"
                    value={tipoContratoLabel(contrato?.tipoContrato)}
                    width="100%"
                    disabled
                  />

                  <TextFieldBase
                    label="Valor Atual (R$)"
                    value={Number(contrato?.valorAtual ?? 0).toFixed(2)}
                    width="100%"
                    textAlign="center"
                    disabled
                  />

                  <Box sx={{ width: "100%" }} />
                </Box>

                {Number(contrato?.statusAtual ?? 0) === 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />

                    <Box
                      sx={{
                        display: "grid",
                        gap: 2,
                        width: "100%",
                        maxWidth: "100%",
                        overflow: "hidden",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: "repeat(2, minmax(0, 1fr))",
                          lg: "repeat(4, minmax(0, 1fr))",
                        },
                      }}
                    >
                      <Box sx={{ gridColumn: { xs: "1 / -1", lg: "1 / -1" } }}>
                        <InputLabelSelect id="motivoCancelamentoOpcao">Motivo do Cancelamento</InputLabelSelect>
                        <SelectBase
                          id="motivoCancelamentoOpcao"
                          label="Motivo do Cancelamento"
                          value={contrato?.motivoCancelamentoOpcao ?? 0}
                          width="100%"
                          disabled
                          isShow
                        >
                          {motivosCancelamento.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.descricaoMotivo}
                            </option>
                          ))}
                        </SelectBase>
                      </Box>

                      <Box sx={{ gridColumn: { xs: "1 / -1", lg: "1 / -1" } }}>
                        <TextFieldBase
                          label="Detalhes do Cancelamento"
                          value={contrato?.motivoCancelamento ?? ""}
                          width="100%"
                          multiline
                          rows={3}
                          disabled
                        />
                      </Box>
                    </Box>
                  </>
                )}
              </Container>
            </FormSectionBox>

            <Divider sx={{ my: 1.5 }} />

            <FormSectionBox>
              <FormSectionTitle label="Dados do Pedido" />

              <Container sx={{ my: 3 }}>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    width: "100%",
                    maxWidth: "100%",
                    overflow: "hidden",
                    paddingTop: 1,
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(4, minmax(0, 1fr))",
                    },
                  }}
                >
                  <TextFieldBase label="Data Entrada Pedido" value={pedidoInfo.dataEntradaPedido} width="100%" textAlign="center" disabled />
                  <TextFieldBase label="Data Liberação Pedido" value={pedidoInfo.dataLiberacaoPedido} width="100%" textAlign="center" disabled />
                  <TextFieldBase label="Faixa Func." value={pedidoInfo.faixaFuncionarios} width="100%" textAlign="center" disabled />
                  <TextFieldBase label="Valor Total" value={pedidoInfo.valorPedido} width="100%" textAlign="center" disabled />

                  {contrato?.sistema === "ACESSO" && (
                    <Box sx={{ gridColumn: { xs: "1 / -1", lg: "span 4" } }}>
                      <TextFieldBase label="Vertical" value={pedidoInfo.tipoVertical} width="100%" disabled />
                    </Box>
                  )}

                  {contrato?.sistema === "PONTO" && (
                    <Box sx={{ gridColumn: { xs: "1 / -1", lg: "span 4" } }}>
                      <TextFieldBase
                        label="Tipo de Plano"
                        value={
                          pedidoInfo.tipoInstalacao === 0
                            ? "Licenciado"
                            : pedidoInfo.tipoInstalacao === 1
                              ? "SaaS (Rep-C)"
                              : pedidoInfo.tipoInstalacao === 2
                                ? "SaaS (Rep-P)"
                                : ""
                        }
                        width="100%"
                        disabled
                      />
                    </Box>
                  )}
                </Box>
              </Container>
            </FormSectionBox>

            <Divider sx={{ my: 1.5 }} />

            {negociacoes.length > 0 && (
              <>
              <FormSectionBox>
                <FormSectionTitle label="Negociações do Contrato" />
                <Container sx={{ my: 3 }}>
                  <TableContainer component={Paper} sx={{ overflow: "visible" }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Período Inicial</StyledTableCell>
                          <StyledTableCell>Período Final</StyledTableCell>
                          <StyledTableCell>Valor Negociado (R$)</StyledTableCell>
                          <StyledTableCell>Motivo</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {negociacoes.map((row, idx) => (
                          <TableRow key={idx}>
                            <StyledTableCell>{toDateBR(row.periodoInicial)}</StyledTableCell>
                            <StyledTableCell>{toDateBR(row.periodoFinal)}</StyledTableCell>
                            <StyledTableCell>{Number(row.valorNegociado ?? 0).toFixed(2)}</StyledTableCell>
                            <StyledTableCell>{row.descricaoMotivo}</StyledTableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Container>
              </FormSectionBox>

              <Divider sx={{ my: 1.5 }} />
              </>
            )}

            <FormSectionBox>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <FormSectionTitle label="Histórico do Contrato" />

                <Stack direction="row" alignItems="center" mt={-5}>
                  <Chip icon={<HistoryIcon />} label={`${histTotal || historico.length} registros`} size="small" variant="outlined" />
                  <Tooltip title={historicoOpen ? "Ocultar" : "Exibir"}>
                    <IconButton onClick={() => setHistoricoOpen((v) => !v)}>
                      {historicoOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>

              <Collapse in={historicoOpen} timeout="auto" unmountOnExit>
                <Container sx={{ my: 3 }}>
                  <Box
                    sx={{
                      position: "relative",
                      paddingLeft: "28px",
                      "&::before": {
                        content: totalItens > 1 ? '""' : "none",
                        position: "absolute",
                        left: "32px",
                        top: "10px",
                        bottom: "10px",
                        borderLeft: "2px dashed rgba(0, 0, 0, 0.15)",
                        zIndex: 1,
                      },
                    }}
                  >
                    {totalItens === 0 ? (
                      <Typography sx={{ color: "text.secondary", pl: 2 }}>Nenhum histórico encontrado para este contrato.</Typography>
                    ) : (
                      visibleItens.map((h) => {
                        const st = statusLabel(Number(h?.statusContrato ?? 0));

                        return (
                          <Box
                            key={h.id}
                            sx={{
                              position: "relative",
                              display: "grid",
                              gridTemplateColumns: "28px 1fr",
                              columnGap: "12px",
                              padding: "10px 0",
                            }}
                          >
                            <Box
                              sx={{
                                width: "10px",
                                height: "10px",
                                borderRadius: "999px",
                                marginTop: "6px",
                                backgroundColor: "rgba(2, 132, 199, 1)",
                                zIndex: 2,
                              }}
                            />

                            <Paper
                              variant="outlined"
                              sx={{
                                background: "#fff",
                                border: "1px solid rgba(0,0,0,0.08)",
                                borderRadius: "12px",
                                padding: "12px",
                                width: { xr: "100%", sm: "100%", lg: "60rem" },
                              }}
                            >
                              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ gap: 1, mb: 1 }}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                  <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: "13px" }}>
                                    {toDateBR(h.dataAtualizacao || h.dataAtivacao)}
                                  </Typography>

                                  <Chip label={st.label} color={st.color} size="small" />
                                  <Chip label={tipoContratoLabel(h.tipoContrato)} size="small" variant="outlined" />
                                  <Chip label={h.sistema} size="small" variant="outlined" />
                                </Stack>

                                <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                                  R$ {Number(h.valor ?? 0).toFixed(2)}
                                </Typography>
                              </Stack>

                              <Divider sx={{ my: 1.2 }} />

                              <Stack spacing={0.5}>
                                <Typography sx={{ color: "text.secondary" }}>
                                  NS: <b>{h.numeroSerie}</b> • Pedido: <b>{h.pedidoId}</b>
                                </Typography>

                                {h.dataAtivacao && (
                                  <Typography sx={{ color: "text.secondary" }}>
                                    Ativação: <b>{toDateBR(h.dataAtivacao)}</b>
                                    {h.dataCancelamento ? (
                                      <>
                                        {" "}• Cancelamento: <b>{toDateBR(h.dataCancelamento)}</b>
                                      </>
                                    ) : null}
                                  </Typography>
                                )}

                                {(h.motivoCancelamentoOpcao || h.motivoCancelamento) && (
                                  <Typography sx={{ color: "text.secondary" }}>
                                    Motivo:{" "}
                                    <b>
                                      {h.motivoCancelamentoOpcao
                                        ? getMotivoCancelamentoDescricao(h.motivoCancelamentoOpcao)
                                        : "-"}
                                    </b>
                                    {h.motivoCancelamento ? ` • ${h.motivoCancelamento}` : ""}
                                  </Typography>
                                )}
                              </Stack>
                            </Paper>
                          </Box>
                        );
                      })
                    )}
                  </Box>

                  {totalItens > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2, gap: 1.5, flexWrap: "wrap" }}>
                      {hasMoreVisible ? (
                        <Button variant="contained" onClick={handleVerMais} disabled={historicoLoading}>
                          Ver mais
                        </Button>
                      ) : canLoadMoreHistorico ? (
                        <Button variant="contained" onClick={handleCarregarMaisServidor} disabled={historicoLoading}>
                          Carregar mais
                        </Button>
                      ) : (
                        <Typography sx={{ color: "text.secondary" }}>Fim do histórico.</Typography>
                      )}
                    </Box>
                  )}
                </Container>
              </Collapse>
            </FormSectionBox>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, p: 2 }}>
              <Button variant="outlined" onClick={() => navigate(-1)}>
                Voltar
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ViewContrato;
